import { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Seo } from "@/components/common/Seo";
import { captureAppError } from "@/lib/monitoring/sentry";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { useTranslate } from "@/i18n";

/* ─── Schema ─────────────────────────────── */

const contactSchema = z.object({
  name: z.string().trim().max(100).optional().or(z.literal("")),
  email: z
    .string()
    .trim()
    .nonempty("E-post krävs")
    .email("Ogiltig e-postadress")
    .max(255),
  subject: z.string().trim().max(200).optional().or(z.literal("")),
  message: z
    .string()
    .trim()
    .nonempty("Meddelande krävs")
    .max(5000, "Meddelandet är för långt"),
  company: z.string().optional(), // honeypot — validated in onSubmit, not zod
});

type ContactFormValues = z.infer<typeof contactSchema>;

/* ─── Component ──────────────────────────── */

const KontaktPage = () => {
  const t = useTranslate();
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: "", email: "", subject: "", message: "", company: "" },
  });

  const onSubmit = useCallback(
    async (data: ContactFormValues) => {
      // Honeypot check — silently "succeed" for bots
      if (data.company && data.company.length > 0) {
        setSubmitted(true);
        return;
      }

      try {
        // Get user if logged in
        const {
          data: { user },
        } = await supabase.auth.getUser();

        const { error } = await supabase.from("contact_messages").insert({
          name: data.name || null,
          email: data.email,
          subject: data.subject || null,
          message: data.message,
          viewer_user_id: user?.id ?? null,
          page_path: window.location.pathname,
          user_agent: navigator.userAgent,
        });

        if (error) throw error;

        track("contact_submitted", {
          page: "/kontakt",
        });

        setSubmitted(true);
      } catch (err: any) {
        console.error("[KontaktPage] Insert failed:", {
          code: err?.code,
          message: err?.message,
          details: err?.details,
          hint: err?.hint,
        });

        captureAppError(err, { feature: "contact", action: "submit" });

        track("contact_submit_failed", {
          error_code: err?.code ?? "unknown",
          message: err?.message ?? "Unknown error",
        });

        toast({
          title: "Kunde inte skicka",
          description:
            "Det gick inte att skicka just nu. Försök igen eller maila oss på hello@farmly.se.",
          variant: "destructive",
        });
      }
    },
    [],
  );

  const handleReset = useCallback(() => {
    reset();
    setSubmitted(false);
  }, [reset]);

  return (
    <div className="flex min-h-screen flex-col">
      <Seo title={t("contact.seoTitle")} description={t("contact.seoDescription")} />
      <SiteHeader />

      <main className="flex-1 container max-w-xl py-16 md:py-24 px-4">
        <h1 className="font-display text-2xl md:text-3xl font-medium text-foreground mb-2">
          {t("contact.title")}
        </h1>
        <p className="text-muted-foreground leading-relaxed mb-8">
          {t("contact.lead")}
        </p>

        <Card className="border-border/40 shadow-sm">
          <CardContent className="pt-6">
            {submitted ? (
              /* ── Success state ── */
              <div className="py-8 text-center space-y-4">
                <p className="text-foreground font-medium">
                  {t("contact.success")}
                </p>
                <button
                  onClick={handleReset}
                  className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors"
                >
                  {t("contact.newMessage")}
                </button>
              </div>
            ) : (
              /* ── Form ── */
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-5"
                noValidate
              >
                {/* Honeypot — hidden from real users */}
                <div
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}
                >
                  <label htmlFor="company">Company</label>
                  <input
                    id="company"
                    type="text"
                    autoComplete="nope"
                    tabIndex={-1}
                    {...register("company")}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="name">{t("contact.label.name")}</Label>
                  <Input
                    id="name"
                    placeholder={t("contact.placeholder.name")}
                    {...register("name")}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">
                    {t("contact.label.email")} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("contact.placeholder.email")}
                    {...register("email")}
                    aria-invalid={!!errors.email}
                  />
                  {errors.email ? (
                    <p className="text-xs text-destructive">{errors.email.message}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground/70">
                      {t("contact.emailHint")}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="subject">{t("contact.label.subject")}</Label>
                  <Input
                    id="subject"
                    placeholder={t("contact.placeholder.subject")}
                    {...register("subject")}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="message">
                    {t("contact.label.message")} <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    rows={6}
                    placeholder={t("contact.placeholder.message")}
                    {...register("message")}
                    aria-invalid={!!errors.message}
                  />
                  {errors.message && (
                    <p className="text-xs text-destructive">
                      {errors.message.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto"
                >
                  {isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("contact.submit")}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Alternative + privacy */}
        <div className="mt-6 space-y-1 text-center">
          <p className="text-sm text-muted-foreground">
            {t("contact.altIntro")}{" "}
            <a
              href="mailto:hello@farmly.se"
              className="underline underline-offset-4 hover:text-foreground transition-colors"
            >
              hello@farmly.se
            </a>
          </p>
          <p className="text-xs text-muted-foreground/60">
            <a
              href="/integritet"
              className="underline underline-offset-4 hover:text-foreground transition-colors"
            >
              {t("contact.privacyLink")}
            </a>
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default KontaktPage;
